import { useQueryClient } from 'react-query';
import { useCreateCatalogProducts } from '../../../../../../../hooks/requests/use-create-catalog-products';
import { useUpdateCatalogProducts } from '../../../../../../../hooks/requests/use-update-catalog-products';
import { getAccountId } from '../../../../../../../services/user-auth';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import { invalidateProductsQueries } from '../../../../../../../utils/query-invalidation';
import { CatalogProductsFormData } from '../../create-product-dialog-types';
import { isNotNullOrUndefined, validateNotNullOrUndefined } from '@vy/utils';
import { type CatalogProducts, NewCatalogProduct } from '@vy/types';
import {
  buildNetSuiteIntegrationEntities,
  buildQuickBooksIntegrationEntities,
  buildXeroIntegrationEntities,
  buildPaddleIntegrationEntities,
  buildAnrokIntegrationEntities,
} from './build-integration-entities';

type UseCatalogProductSubmissionProps = {
  onSuccess?: (productName: string) => void;
  onClose: () => void;
  resetCallerState: () => void;
  editProduct?: CatalogProducts;
};

export const useCatalogProductSubmission = ({
  onSuccess,
  onClose,
  resetCallerState,
  editProduct,
}: UseCatalogProductSubmissionProps) => {
  const queryClient = useQueryClient();
  const createCatalogProducts = useCreateCatalogProducts();
  const updateCatalogProducts = useUpdateCatalogProducts();
  const isEditMode = isNotNullOrUndefined(editProduct);

  const submitCatalogProduct = async (data: CatalogProductsFormData) => {
    const accountId = getAccountId();

    validateNotNullOrUndefined(accountId, 'Account ID is required');
    const catalogProductPayload = mapProductsToIntegrationEntity(accountId, data);

    const onSuccessHandler = async () => {
      await invalidateProductsQueries(queryClient);

      if (onSuccess) {
        onSuccess(data.name);
      }

      onClose();
      resetCallerState();
    };

    if (isEditMode && isNotNullOrUndefined(editProduct)) {
      await updateCatalogProduct(
        editProduct.id,
        catalogProductPayload,
        onSuccessHandler,
        updateCatalogProducts,
      );
    } else {
      await createCatalogProduct(catalogProductPayload, onSuccessHandler, createCatalogProducts);
    }
  };

  return { submitCatalogProduct };
};

function createCatalogProduct(
  data: NewCatalogProduct,
  onSuccessHandler: () => void,
  createCatalogProducts: ReturnType<typeof useCreateCatalogProducts>,
) {
  return runMutation(
    createCatalogProducts,
    data,
    {
      pending: 'Creating catalog product...',
      success: 'Catalog product created successfully',
      error: (toastProps) => (toastProps?.data as Error)?.message || 'Failed to create catalog product',
    },
    {
      onSuccess: onSuccessHandler,
    },
  );
}

function updateCatalogProduct(
  existingCatalogProductId: string,
  data: NewCatalogProduct,
  onSuccessHandler: () => void,
  updateCatalogProducts: ReturnType<typeof useUpdateCatalogProducts>,
) {
  return runMutation(
    updateCatalogProducts,
    {
      catalogProductId: existingCatalogProductId,
      updateCatalogProductsRequest: data,
    },
    {
      pending: 'Updating catalog product...',
      success: 'Catalog product updated successfully',
      error: (toastProps) => (toastProps?.data as Error)?.message || 'Failed to update catalog product',
    },
    {
      onSuccess: onSuccessHandler,
    },
  );
}

function mapProductsToIntegrationEntity(
  accountId: string,
  formValue: CatalogProductsFormData,
): NewCatalogProduct {
  const netSuiteEntities = buildNetSuiteIntegrationEntities(formValue.netSuiteProduct, formValue.netSuiteClass);
  const quickBooksEntities = buildQuickBooksIntegrationEntities(formValue.quickBooksProduct);
  const xeroEntities = buildXeroIntegrationEntities(formValue.xeroProduct);
  const paddleEntities = buildPaddleIntegrationEntities(formValue.paddleProduct);
  const anrokEntities = buildAnrokIntegrationEntities(formValue.anrokProduct);

  return {
    ...formValue,
    accountId,
    integrationEntities: [...netSuiteEntities, ...quickBooksEntities, ...xeroEntities, ...paddleEntities, ...anrokEntities],
  };
}
